import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';

import StatusDot from 'components/Relatorios/shared/Aprovacao/StatusDot';
import useRelatorioCalendario from 'hooks/Relatorios/Aprovacao/useRelatorioCalendario';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { RelatorioAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';

import 'components/Relatorios/shared/Aprovacao/calendario.scss';

interface DataAplicada {
  ano: number;
  mes: number;
  dia: number;
}

export interface CalendarioAprovacao {
  /** Props extras do `DatePicker` (vazio com o informe desabilitado). */
  datePickerProps: Record<string, unknown>;
  mostraLegenda: boolean;
  mesVisivel: Date;
  /** Chamar quando o usuário escolhe um dia: acompanha o mês do dia escolhido. */
  onDataSelecionada: (date: Date) => void;
  /** Reposiciona o mês visível (no-op se já é o mês). O hook já chama no efeito D8. */
  resync: (ano: number, mes: number) => void;
}

/**
 * Calendário do painel de controle: dono do `mesVisivel`, consulta o calendário de
 * publicação (variante **aplicada**, não o rascunho) e monta as props do DatePicker:
 * admin vê bolinhas de status; cliente só seleciona dias publicados.
 *
 * D8: quando a data aplicada muda por fora do painel (ex.: clique no calendário do
 * PainelAprovacao / `irParaData`), o mês visível acompanha — deps primitivas
 * [ano, mes, dia], então não há loop nem resync de cenário/modo.
 */
export default function useCalendarioAprovacao(
  config: RelatorioAprovacaoConfig,
  dataAplicada: DataAplicada,
  varianteAplicada: string,
): CalendarioAprovacao {
  const isAdmin = useSelector(selectUserIsAdmin);
  const habilitado = config.habilitado();
  const { ano, mes, dia } = dataAplicada;

  const [mesVisivel, setMesVisivel] = useState(() => new Date(ano, mes - 1, 1));

  const { statusDoDia } = useRelatorioCalendario(config, mesVisivel, varianteAplicada);

  const mesmoMes = (a: Date, y: number, m: number) => (
    a.getFullYear() === y && a.getMonth() === m
  );

  const resync = useCallback((y: number, m: number) => {
    setMesVisivel((atual) => (mesmoMes(atual, y, m - 1) ? atual : new Date(y, m - 1, 1)));
  }, []);

  // `dia` só entra nas deps para deixar explícito o gatilho D8 (data aplicada mudou).
  useEffect(() => {
    resync(ano, mes);
  }, [ano, mes, dia, resync]);

  const onDataSelecionada = useCallback((date: Date) => {
    setMesVisivel((atual) => (
      mesmoMes(atual, date.getFullYear(), date.getMonth())
        ? atual
        : new Date(date.getFullYear(), date.getMonth(), 1)
    ));
  }, []);

  const handleMonthChange = useCallback((date: Date) => {
    setMesVisivel(new Date(date.getFullYear(), date.getMonth(), 1));
  }, []);

  const mostraAdmin = habilitado && isAdmin;
  const mostraCliente = habilitado && !isAdmin;

  const renderDayContents = useCallback((d: number, date: Date) => {
    const status = statusDoDia(date);
    return (
      <>
        {d}
        {status && (
          <span className="relatorio-aprovacao-calendario__dia-dot">
            <StatusDot status={status} />
          </span>
        )}
      </>
    );
  }, [statusDoDia]);

  // Dias de outro mês que a grade mostra (ex.: 27 a 30/09 na grade de outubro) não têm status
  // carregado — o calendário é consultado só para o mês visível. Para o cliente eles ficam
  // indisponíveis: antes caíam no "sem status = libera" abaixo e apareciam clicáveis sem informe
  // (relato do Marcelo, 14/10/2026). Ao trocar de mês, os dias daquele mês ganham status.
  const foraDoMesVisivel = useCallback(
    (date: Date) => !mesmoMes(mesVisivel, date.getFullYear(), date.getMonth()),
    [mesVisivel],
  );

  // Enquanto o calendário do mês carrega ou dá erro, não filtra os dias do mês — travar a
  // seleção nesse meio-tempo prejudicaria mais do que ajuda (o PublicacaoGate já
  // protege o conteúdo publicado/não publicado).
  const filterDate = useCallback((date: Date) => {
    if (foraDoMesVisivel(date)) return false;
    const status = statusDoDia(date);
    if (status === undefined) return true;
    return status === 'publicado';
  }, [statusDoDia, foraDoMesVisivel]);

  // `__dia` dá `position: relative` à célula (âncora do dot) só nos calendários da
  // aprovação — sem vazar para outros datepickers do app.
  const dayClassNameAdmin = useCallback(() => 'relatorio-aprovacao-calendario__dia', []);

  const dayClassName = useCallback((date: Date) => {
    const status = statusDoDia(date);
    return foraDoMesVisivel(date) || (status !== undefined && status !== 'publicado')
      ? 'relatorio-aprovacao-calendario__dia relatorio-aprovacao-calendario__dia-indisponivel'
      : 'relatorio-aprovacao-calendario__dia';
  }, [statusDoDia, foraDoMesVisivel]);

  const datePickerProps = useMemo(() => {
    if (mostraAdmin) {
      return {
        renderDayContents,
        dayClassName: dayClassNameAdmin,
        onMonthChange: handleMonthChange,
        onYearChange: handleMonthChange,
      };
    }
    if (mostraCliente) {
      return {
        filterDate, dayClassName, onMonthChange: handleMonthChange, onYearChange: handleMonthChange,
      };
    }
    return {};
  }, [mostraAdmin, mostraCliente, renderDayContents, dayClassNameAdmin, filterDate, dayClassName,
    handleMonthChange]);

  return {
    datePickerProps,
    mostraLegenda: mostraAdmin,
    mesVisivel,
    onDataSelecionada,
    resync,
  };
}
